/**
 * Edge Auth Library - JWT to API Key Exchange for Cloudflare Workers
 *
 * Provides lightweight session management by exchanging heavy JWTs for
 * fast API keys optimized for edge computing environments.
 */

import { nanoid } from "nanoid";
import {
  jwtVerify,
  createRemoteJWKSet,
  // importSPKI,
  // importX509,
  type JWTPayload,
} from "jose";

// Types
export const KEY_SCOPE_KINDS = ["account", "workspace"] as const;
export type KeyScopeKind = (typeof KEY_SCOPE_KINDS)[number];

/** The account or workspace an API key (and its session) is bound to. */
export type KeyScope = { kind: KeyScopeKind; id: string };

/** Builds a KeyScope from its parts; throws on an unknown kind or an empty id. */
export function toKeyScope(kind: string, id: string): KeyScope {
  if (!(KEY_SCOPE_KINDS as readonly string[]).includes(kind)) {
    throw new Error(`Invalid key scope kind: ${kind}`);
  }
  if (!id || id.includes(":")) {
    throw new Error("Invalid key scope id");
  }
  return { kind: kind as KeyScopeKind, id };
}

/** Canonical `${kind}:${id}` form of a KeyScope. */
export function formatKeyScope(scope: KeyScope): string {
  return `${scope.kind}:${scope.id}`;
}

// Type alias (not interface) so it stays assignable to Record<string, unknown>.
export type SessionData = {
  userId: string;
  username: string; // User's username from JWT
  email: string; // User's email from JWT
  displayName: string; // User's display name from JWT
  avatar: string; // User's avatar/icon from JWT
  scope: KeyScope;
  participantId: string;
  role: string; // User's role (guest, registered, admin)
  expires: number;
  createdAt: string;
};

export interface AuthSession {
  apiKey: string;
  expiresIn: number;
  sessionData: SessionData;
}

export interface EdgeAuthConfig {
  // jwtSecret: string; // For symmetric algorithms (HS256)
  // publicKeyPem: string; // For asymmetric algorithms (RS256) - Casdoor public key or certificate
  jwksUrl: string; // OIDC JWKS endpoint URL (e.g. https://auth.example.com/.well-known/jwks)
  apiKeyLength?: number; // default 32 (longer)
  encryptionKey: string; // For API key encryption (base64url encoded 32-byte key)
}

/**
 * Generate new API key with encrypted scope
 * Format: base64url(encrypt(kind:id:key))
 *
 * To decrypt and extract:
 * 1. Base64url decode the API key
 * 2. Decrypt using AES-256-GCM with the encryption key
 * 3. Split decrypted string on ':' to get [kind, id, key]
 */
async function generateApiKey(
  scope: KeyScope,
  encryptionKey: string,
  apiKeyLength: number = 32,
): Promise<string> {
  if (!encryptionKey) {
    throw new Error("Encryption key required for API key generation");
  }
  if (encryptionKey.length !== 32) {
    throw new Error("Encryption key must be 32 bytes");
  }

  const key = nanoid(apiKeyLength);
  const plaintext = `${formatKeyScope(toKeyScope(scope.kind, scope.id))}:${key}`;

  // Import encryption key
  const encryptionKeyBytes = Buffer.from(encryptionKey, "base64url");
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encryptionKeyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    cryptoKey,
    plaintextBytes,
  );

  // Combine IV + ciphertext and encode
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return Buffer.from(combined).toString("base64url");
}

/**
 * Decrypt API key to extract its typed scope and key.
 * Throws on any plaintext other than `${kind}:${id}:${key}`.
 */
export async function decryptApiKey(
  encryptedApiKey: string,
  encryptionKey: string,
): Promise<{ scope: KeyScope; key: string }> {
  if (!encryptionKey) {
    throw new Error("Encryption key required for API key decryption");
  }
  if (encryptionKey.length !== 32) {
    throw new Error("Encryption key must be 32 bytes");
  }

  // Decode from base64url
  const combined = Buffer.from(encryptedApiKey, "base64url");

  // Extract IV and ciphertext
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  // Import encryption key
  const encryptionKeyBytes = Buffer.from(encryptionKey, "base64url");
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encryptionKeyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  // Decrypt
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    cryptoKey,
    ciphertext,
  );

  const decrypted = new TextDecoder().decode(plaintext);
  const parts = decrypted.split(":");

  const [kind, id, key] = parts;
  if (parts.length !== 3 || !kind || !id || !key) {
    throw new Error("Invalid API key format");
  }

  return { scope: toKeyScope(kind, id), key };
}

// Client-side auth manager
export class EdgeAuthClient {
  private apiKey: string | null = null;
  private sessionData: SessionData | null = null;
  private baseUrl: string;
  private authPathPrefix: string;

  constructor(baseUrl: string, authPathPrefix: string = "/auth") {
    this.baseUrl = baseUrl.replace(/\/$/, ""); // Remove trailing slash
    this.authPathPrefix = authPathPrefix;
    // Cookie-only mode - no localStorage loading needed
  }

  /**
   * Exchange JWT for lightweight API key
   * @param jwt - The JWT token to exchange
   * @param clearJWT - Callback to clear JWT from workspace storage after successful exchange
   */
  async exchangeJWT(
    jwt: string,
    clearJWT?: () => Promise<void>,
  ): Promise<AuthSession> {
    const response = await fetch(`${this.baseUrl}${this.authPathPrefix}/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Auth exchange failed: ${response.statusText}`);
    }

    const session = (await response.json()) as AuthSession;

    // Store session
    this.apiKey = session.apiKey;
    this.sessionData = session.sessionData;
    // this.saveToStorage();

    // Clear JWT from workspace storage after successful exchange
    if (clearJWT) {
      try {
        await clearJWT();
        // console.log('🗑️ JWT cleared from storage after successful exchange');
      } catch (error) {
        console.warn("Failed to clear JWT after exchange:", error);
        // Don't throw - session exchange was successful
      }
    }

    return session;
  }

  /**
   * Validate current API key
   */
  async validateSession(): Promise<boolean> {
    try {
      // Cookie-only validation - no API key needed
      const response = await fetch(`${this.baseUrl}${this.authPathPrefix}/validate`, {
        credentials: "include", // ✅ Include cookies
      });

      if (response.ok) {
        const sessionData = (await response.json()) as SessionData;
        this.sessionData = sessionData;
        return true;
      }
    } catch (error) {
      console.warn("Session validation failed:", error);
    }

    this.clearSession();
    return false;
  }

  /**
   * Refresh session with new JWT
   */
  async refreshSession(newJWT: string): Promise<AuthSession> {
    if (!this.apiKey) {
      return this.exchangeJWT(newJWT);
    }

    const response = await fetch(`${this.baseUrl}${this.authPathPrefix}/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": this.apiKey,
        Authorization: `Bearer ${newJWT}`,
      },
    });

    if (!response.ok) {
      // Fallback to full exchange
      return this.exchangeJWT(newJWT);
    }

    const session = (await response.json()) as AuthSession;
    this.apiKey = session.apiKey;
    this.sessionData = session.sessionData;
    // this.saveToStorage();

    return session;
  }

  /**
   * Logout and revoke session
   */
  async logout(): Promise<void> {
    try {
      // Cookie-only logout - no API key needed
      await fetch(`${this.baseUrl}${this.authPathPrefix}/logout`, {
        method: "POST",
        credentials: "include", // ✅ Include cookies
      });
    } catch (error) {
      console.warn("Logout request failed:", error);
    }

    this.clearSession();
  }

  /**
   * Get auth headers for API requests
   */
  getAuthHeaders(): Record<string, string> {
    // Cookie-only mode - no headers needed, cookies are automatic
    return {};
  }

  /**
   * Get current session data
   */
  getSession(): { apiKey: string; sessionData: SessionData } | null {
    if (!this.sessionData) return null;

    return {
      apiKey: this.apiKey || "",
      sessionData: this.sessionData,
    };
  }

  /**
   * Check if session is expired
   */
  isSessionExpired(): boolean {
    if (!this.sessionData) return true;
    return Date.now() > this.sessionData.expires;
  }

  // Private methods
  // private saveToStorage(): void {
  //   if (typeof localStorage !== 'undefined' && this.apiKey && this.sessionData) {
  //     localStorage.setItem('edge_auth_key', this.apiKey);
  //     localStorage.setItem('edge_auth_session', JSON.stringify(this.sessionData));
  //   }
  // }

  // Cookie-only mode - no localStorage methods needed

  private clearSession(): void {
    this.apiKey = null;
    this.sessionData = null;

    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("edge_auth_key");
      localStorage.removeItem("edge_auth_session");
    }
  }
}


// Server-side utilities for Cloudflare Workers
export class EdgeAuthServer {
  private config: EdgeAuthConfig;

  constructor(config: EdgeAuthConfig) {
    this.config = {
      apiKeyLength: 32, // Longer default
      ...config,
    };
  }

  /**
   * Verify JWT and extract claims
   * Supports both symmetric (HS256) and asymmetric (RS256) algorithms
   * Handles both public keys and certificates from Casdoor
   */
  async verifyJWT(jwt: string): Promise<JWTPayload> {
    try {
      // if (this.config.publicKeyPem) {
      //   if (this.config.publicKeyPem.includes('BEGIN CERTIFICATE')) {
      //     key = await importX509(this.config.publicKeyPem, 'RS256');
      //   } else if (this.config.publicKeyPem.includes('BEGIN PUBLIC KEY')) {
      //     key = await importSPKI(this.config.publicKeyPem, 'RS256');
      //   } else {
      //     throw new Error('Invalid PEM format: must be either CERTIFICATE or PUBLIC KEY');
      //   }
      // } else if (this.config.jwtSecret) {
      //   key = new TextEncoder().encode(this.config.jwtSecret);
      // } else {
      //   throw new Error('No JWT verification key provided (publicKeyPem or jwtSecret required)');
      // }
      const jwks = createRemoteJWKSet(new URL(this.config.jwksUrl));
      const { payload } = await jwtVerify(jwt, jwks);
      return payload;
    } catch (error) {
      throw new Error(
        `EdgeAuth JWT verification failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Generate new API key with encrypted scope
   * Format: base64url(encrypt(kind:id:key))
   */
  async generateApiKey(scope: KeyScope): Promise<string> {
    return generateApiKey(
      scope,
      this.config.encryptionKey,
      this.config.apiKeyLength,
    );
  }

  /**
   * Decrypt API key to extract its typed scope and key
   */
  async decryptApiKey(
    encryptedApiKey: string,
  ): Promise<{ scope: KeyScope; key: string }> {
    return decryptApiKey(encryptedApiKey, this.config.encryptionKey);
  }

  /**
   * Create session data from JWT payload for a resolved scope, lifetime and role
   */
  createSessionData(
    payload: JWTPayload,
    scope: KeyScope,
    ttlSeconds: number,
    role: string,
  ): SessionData {
    const now = Date.now();
    const expires = now + ttlSeconds * 1000;

    return {
      userId: payload.sub || "unknown",
      username: (payload.name as string) || payload.sub || "unknown",
      email: (payload.email as string) || "unknown@example.com",
      displayName:
        (payload.displayName as string) ||
        (payload.name as string) ||
        payload.sub ||
        "Unknown User",
      avatar: (payload.avatar as string) || (payload.picture as string) || "",
      scope,
      participantId:
        (payload.participant_id as string) || payload.sub || "unknown",
      role,
      expires,
      createdAt: new Date(now).toISOString(),
    };
  }

  /**
   * Check if session is expired
   */
  isSessionExpired(sessionData: SessionData): boolean {
    return Date.now() > sessionData.expires;
  }
}

// Utility function for easy setup
export function createEdgeAuth(baseUrl: string, authPathPrefix?: string): EdgeAuthClient {
  return new EdgeAuthClient(baseUrl, authPathPrefix);
}

// Export types for external use
export type { JWTPayload };
